import { useState, type ChangeEvent, type FormEvent } from 'react';
import type { Device, DeviceNetwork, ProvisioningTemplate } from '@arcnet/contracts';
import { useSaveDevice } from '../../lib/api/hooks-extension.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useProvisioningUrl } from '../../lib/api/hooks-provisioning.js';
import { useAddPhone, useTemplates } from '../../lib/api/hooks-templates.js';
import { ApiClientError } from '../../lib/api/client.js';
import { Toggle } from '../../components/ui/Form.js';

/**
 * A desk phone on an extension: its MAC and its template (docs/29, owner 15 September 2026).
 *
 * The template decides the model, the keys and the network settings phones share, so this
 * form asks only for what is the phone's own: which physical phone (its MAC), what it is
 * called, and which template. The extension it is added from is the phone's only line
 * (owner, 15 September 2026): anybody else appears on it as a busy lamp key in its template,
 * never as another SIP account. A phone with a static address keeps its own.
 *
 * Choosing a template for another model changes the phone's model with it, which is how a
 * phone recorded as the wrong model is put right.
 */

interface AddressDraft { ip: string; netmask: string; gateway: string; dns1: string; dns2: string; vlan: string }

const addressOf = (n: Device['network']): AddressDraft => ({
  ip: n?.ip ?? '', netmask: n?.netmask ?? '', gateway: n?.gateway ?? '',
  dns1: n?.dns[0] ?? '', dns2: n?.dns[1] ?? '', vlan: n?.vlan_id ? String(n.vlan_id) : '',
});

const staticNetwork = (a: AddressDraft): DeviceNetwork => ({
  mode: 'static',
  ip: a.ip.trim() || null, netmask: a.netmask.trim() || null, gateway: a.gateway.trim() || null,
  dns: [a.dns1, a.dns2].map((x) => x.trim()).filter(Boolean),
  vlan_id: a.vlan.trim() ? Number(a.vlan) : null,
});

/** Nothing of the phone's own: the template decides DHCP and the VLAN. */
const TEMPLATE_NETWORK: DeviceNetwork = { mode: 'unmanaged', ip: null, netmask: null, gateway: null, dns: [], vlan_id: null };

export function DeviceEditor({ tenantId, existing, onDone, seedExtensionId }: {
  tenantId: string; existing: Device | null; onDone: () => void;
  seedExtensionId?: string;
}) {
  const add = useAddPhone(tenantId);
  const save = useSaveDevice(tenantId);
  const templates = useTemplates(tenantId);
  const extensions = useExtensions(tenantId, { limit: 500 });
  const genericUrl = useProvisioningUrl();

  const line1 = existing?.lines.find((l) => l.line_number === 1) ?? null;
  const seatId = line1?.extension_id ?? seedExtensionId ?? '';
  const seat = (extensions.data?.data ?? []).find((e) => e.id === seatId) ?? null;
  /** Accounts a phone was given before only its own extension could be one. Saving drops them. */
  const others = (existing?.lines ?? []).filter((l) => l.line_number > 1);

  const [mac, setMac] = useState(existing?.mac_address ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [templateId, setTemplateId] = useState(existing?.template_id ?? '');
  const wasStatic = existing?.network?.mode === 'static';
  const [ownAddress, setOwnAddress] = useState(wasStatic);
  const [address, setAddress] = useState<AddressDraft>(() => addressOf(existing?.network));
  const [issued, setIssued] = useState<{
    url: string | null; credential: { username: string; realm: string; password: string } | null;
  } | null>(null);

  // Another extension's own copy is not offered here.
  const choices = (templates.data?.data ?? []).filter((t) => t.extension_id === null || t.extension_id === seatId);
  const groups = ([
    ['This account', choices.filter((t) => t.tenant_id !== null && t.extension_id === null)],
    ['Every account', choices.filter((t) => t.tenant_id === null)],
    [`${seat?.number ?? 'This extension'}’s own`, choices.filter((t) => t.extension_id !== null)],
  ] as Array<[string, ProvisioningTemplate[]]>).filter(([, list]) => list.length > 0);
  const chosen = choices.find((t) => t.id === templateId) ?? null;

  const busy = add.isPending || save.isPending;
  const failed = add.error ?? save.error;
  const fieldErrors = failed instanceof ApiClientError ? failed.fieldErrors : {};
  const hex = mac.replace(/[^0-9a-fA-F]/g, '');
  const macValid = hex.length === 12;
  const addressIncomplete = ownAddress && (!address.ip.trim() || !address.netmask.trim() || !address.gateway.trim());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!existing) {
      add.mutate({
        extensionId: seatId, mac_address: mac.trim(), template_id: templateId, label: label.trim() || null,
        ...(ownAddress ? { network: staticNetwork(address) } : {}),
      }, { onSuccess: (r) => setIssued({ url: r.provisioning_url, credential: r.sip_credential }) });
      return;
    }
    const templateChanged = templateId !== '' && templateId !== existing.template_id;
    save.mutate({
      id: existing.id,
      label: label.trim() || null,
      mac_address: mac.trim(),
      // A template for another model brings its model with it; the API checks the two agree.
      ...(templateChanged && chosen ? { template_id: templateId, handset_model_id: chosen.handset_model_id } : {}),
      ...(ownAddress ? { network: staticNetwork(address) } : wasStatic ? { network: TEMPLATE_NETWORK } : {}),
      ...(line1 ? {
        lines: [{ extension_id: line1.extension_id, line_number: 1, ...(line1.label ? { label: line1.label } : {}) }],
      } : {}),
    }, {
      onSuccess: (saved) => {
        // A new MAC is a replaced phone: its new password is shown once.
        if (saved?.sip_credential) setIssued({ url: null, credential: saved.sip_credential });
        else onDone();
      },
    });
  };

  if (issued) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <h3 className="arc-h2" style={{ marginBottom: 4 }}>{issued.url ? 'Phone added' : 'Phone replaced'}</h3>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          Point the phone at <strong className="arc-mono">{genericUrl}</strong>, the same address for
          every phone and the one for Yealink RPS, Fanvil FDPS or DHCP option 66. It may fetch for the
          next 30 minutes, and then from the address it used.
          {issued.url ? ' Or use this phone’s own link, shown once and never again:' : ''}
        </p>
        {issued.url && (
          <div className="arc-row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <code className="arc-mono" style={{
              userSelect: 'all', fontSize: 12, padding: '7px 10px',
              background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)', wordBreak: 'break-all',
            }}>{issued.url}</code>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => void navigator.clipboard?.writeText(issued.url ?? '')}>
              Copy
            </button>
          </div>
        )}
        {issued.credential && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--arc-border)' }}>
            <div style={{ fontWeight: 550, marginBottom: 6 }}>Or configure it by hand</div>
            <dl className="arc-kv">
              <dt>SIP username</dt><dd className="arc-mono">{issued.credential.username}</dd>
              <dt>Domain</dt><dd className="arc-mono">{issued.credential.realm}</dd>
              <dt>Password</dt>
              <dd className="arc-row" style={{ gap: 8 }}>
                <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>{issued.credential.password}</span>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => void navigator.clipboard?.writeText(issued.credential?.password ?? '')}>
                  Copy
                </button>
              </dd>
            </dl>
          </div>
        )}
        <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => { setIssued(null); onDone(); }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.label ?? existing.mac_address ?? 'phone'}` : `Add a phone to ${seat?.number ?? 'this extension'}`}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-mac">MAC address</label>
          <input id="dv-mac" className="arc-input arc-mono" value={mac} required aria-invalid={mac !== '' && !macValid}
            onChange={(e) => setMac(e.target.value)} placeholder="805e:c04d:1f2a" />
          {fieldErrors['mac_address']
            ? <p className="arc-error">{fieldErrors['mac_address']}</p>
            : <p className={mac === '' || macValid ? 'arc-hint' : 'arc-error'}>
                {mac === '' || macValid
                  ? existing?.mac_address ? 'A different MAC is a replaced phone: it gets a new password.' : 'Printed on the underside of the phone.'
                  : 'Needs 12 hex digits.'}
              </p>}
        </div>
        <div className="arc-field" style={{ margin: 0, gridColumn: 'span 2' }}>
          <label className="arc-label" htmlFor="dv-template">Template</label>
          <select id="dv-template" className="arc-input" value={templateId} required={!existing}
            onChange={(e) => setTemplateId(e.target.value)}>
            <option value="" disabled={!existing}>
              {existing && !existing.template_id ? 'None — laid out by its role' : 'Choose a template…'}
            </option>
            {groups.map(([group, list]) => (
              <optgroup key={group} label={group}>
                {list.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.vendor_label} {t.model}</option>)}
              </optgroup>
            ))}
          </select>
          {fieldErrors['template_id']
            ? <p className="arc-error">{fieldErrors['template_id']}</p>
            : <p className="arc-hint">
                {chosen
                  ? `${chosen.keys.length} key${chosen.keys.length === 1 ? '' : 's'}`
                    + (existing && existing.handset_model_id !== chosen.handset_model_id ? ` · the phone becomes a ${chosen.model}` : '')
                  : choices.length === 0 ? 'No templates yet. Make one under Phone templates.' : 'Its model, keys and network.'}
              </p>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-label">Name</label>
          <input id="dv-label" className="arc-input" value={label} maxLength={120}
            onChange={(e) => setLabel(e.target.value)} placeholder="Reception desk" />
        </div>
      </div>

      {others.length > 0 && (
        <p className="arc-hint" style={{ marginTop: -6, marginBottom: 14 }}>
          {others.map((l) => l.extension_number).join(' and ')} {others.length === 1 ? 'is' : 'are'} also
          an account on this phone. Saving leaves only {seat?.number ?? 'its own extension'}; give{' '}
          {others.length === 1 ? 'it' : 'them'} busy lamp keys in the template instead.
        </p>
      )}

      <div className="arc-field">
        <Toggle id="dv-own-address" label="Its own static address" checked={ownAddress} onChange={setOwnAddress}
          hint="Otherwise the template decides DHCP and the voice VLAN." />
        {ownAddress && <AddressFields value={address} onChange={setAddress} />}
      </div>

      <div className="arc-row" style={{ gap: 8 }}>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-ghost" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={busy || !macValid || (!existing && !templateId) || addressIncomplete || !seatId}>
          {busy ? 'Saving…' : existing ? 'Save changes' : 'Add phone'}
        </button>
      </div>
    </form>
  );
}

/**
 * The phone's own address, for a site without DHCP. A wrong one takes the phone off the
 * network, where it cannot fetch the correction, so the hint says so where it is typed.
 */
function AddressFields({ value, onChange }: { value: AddressDraft; onChange: (d: AddressDraft) => void }) {
  const field = (key: keyof AddressDraft, label: string, placeholder: string) => (
    <div className="arc-field" style={{ margin: 0 }}>
      <label className="arc-label" htmlFor={`dv-net-${key}`}>{label}</label>
      <input id={`dv-net-${key}`} className="arc-input arc-mono" value={value[key]} placeholder={placeholder}
        inputMode={key === 'vlan' ? 'numeric' : 'decimal'}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.target.value })} />
    </div>
  );
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 10 }}>
        {field('ip', 'Address', '10.0.5.20')}
        {field('netmask', 'Netmask', '255.255.255.0')}
        {field('gateway', 'Gateway', '10.0.5.1')}
        {field('dns1', 'DNS', '10.0.5.2')}
        {field('dns2', 'Second DNS', 'optional')}
        {field('vlan', 'Voice VLAN', 'none')}
      </div>
      <p className="arc-hint">Written into the phone. A wrong address takes it off the network, where it cannot fetch the correction.</p>
    </>
  );
}
